import { useState } from 'react';
import { FirebaseError } from 'firebase/app';
import { SolidButton } from '../Button';
import CustomInput from '../CustomInput';
import { P } from '../Text';
import { useTranslation } from 'next-i18next/pages';
import { useRouter } from 'next/router';
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInAnonymously,
} from 'firebase/auth';
import { auth } from 'config/initAuth';
import useUpdateOrderFirebaseUserId from 'hooks/useUpdateOrderFirebaseUserId';
import {
  anonymousToken,
  movePackageBookings,
} from 'hooks/usePackageMyBookings';

interface Props {
  closeModal: () => void;
  content?: any;
}

interface ILoginFormIF {
  email: string;
  password: string;
}

const LoginForm: React.FC<Props> = (props: Props) => {
  const { closeModal, content } = props;
  const router = useRouter();
  const { fetchData } = useUpdateOrderFirebaseUserId();
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [formState, setFormState] = useState<ILoginFormIF>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleChange = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleResetPassword = async () => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, formState.email);
      setForgotPasswordSuccess(true);
    } catch (err: any) {
      setError(t(err?.message || 'Could not send forgot password email'));
    }
  };

  const onLogin = async () => {
    if (formState.email && formState.password) {
      // Taken while the anonymous user is still signed in (WHIT-483).
      const anonymous = await anonymousToken(auth.currentUser);
      signInWithEmailAndPassword(auth, formState.email, formState.password)
        .then(creds => {
          movePackageBookings(anonymous, creds.user);
          const { id } = router.query;
          if (
            (router.pathname === '/bookings' ||
              router.pathname === '/notifications') &&
            creds?.user?.uid &&
            id
          ) {
            // Update the user id for the order. Deferred a tick, as before,
            // so this replace still lands after resumeLogin()'s push.
            Promise.resolve().then(() => {
              fetchData(id as string, creds.user);
              setError(null);
              closeModal();
              router.replace(router.pathname);
            });
          }
          resumeLogin();
          setError(null);
          closeModal();
        })
        .catch((err: FirebaseError) => {
          setFormState({ ...formState, password: '' });
          switch (err.code) {
            case 'auth/wrong-password':
              setError(t('Password or username is not valid'));
              break;
            case 'auth/user-not-found':
              setError(t('User not found'));
              break;
            case 'auth/invalid-password':
              setError(t('Invalid password entered'));
              break;
            case 'auth/invalid-email':
              setError(t('Invalid email'));
              break;
            case 'auth/internal-error':
            default:
              setError(t('Unknown error happened'));
              break;
          }
        });
    }
  };

  const onFormSubmit = (e: any) => {
    e.preventDefault();
    setForgotPasswordSuccess(false);
    onLogin();
  };

  const resumeLogin = () => {
    // Resumes the login if an url was given
    if (content) {
      const { next, query } = content;
      if (next) {
        router.push({ pathname: next, query: query || '' });
      }
    } else {
      closeModal();
    }
  };

  const handleContinueWithoutRegistration = () => {
    const handleAnonymousLogin = async () => {
      // Sign in the user anonymously and continue the login
      await signInAnonymously(auth);
      resumeLogin();
    };

    handleAnonymousLogin();
  };

  return (
    <form
      className="flex flex-col space-y-4 mt-4 w-full md:w-4/5"
      onSubmit={onFormSubmit}>
      {error && <P className="text-red-600">{error}</P>}
      {forgotPasswordSuccess && (
        <P className="text-green-600">
          {t('Password reset email sent. Please check your inbox')}
        </P>
      )}
      <CustomInput
        value={formState.email}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder={t('Email address') || ''}
        type="email"
        name="email"
        containerClassName="h-12"
      />
      <CustomInput
        value={formState.password}
        onChange={handleChange}
        onEnter={() => {}}
        placeholder={t('Password') || ''}
        type="password"
        name="password"
        containerClassName="h-12"
      />
      <button
        type="button"
        onClick={handleResetPassword}
        className="text-tt-light-gray-purple mx-auto">
        {t('Forgot password')}
      </button>
      <SolidButton buttonType="submit" className="w-full sm:w-3/5 self-center">
        {t('Log in')}
      </SolidButton>
      <div className="relative flex pt-4 pb-2 items-center">
        <div className="flex-grow border-t border-gray-400"></div>
        <span className="flex-shrink mx-4 text-tt-light-gray-purple">
          {/* Looks like what it is, a button, 44px tall; it read as the
              divider's caption (WHIT-494). */}
          <button
            type="button"
            className="min-h-11 px-2 font-medium text-tt-dark-purple underline underline-offset-4 hover:no-underline"
            onClick={handleContinueWithoutRegistration}>
            {t('Continue without log in')}
          </button>
        </span>
        <div className="flex-grow border-t border-gray-400"></div>
      </div>
      {/* <P
        className="text-tt-light-gray-purple w-full flex-1 border-b-[0.5px] border-tt-light-gray-purple-line leading-[0.2em]"
        centered>
        <span className="bg-white pl-2 pr-2 pt-1 pb-1">vagy</span>
      </P>
      <div className="grid grid-rows-2 gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <SolidButton
            className="bg-black hover:bg-[#444444]"
            textColor="text-white">
            Apple ID-val
          </SolidButton>
          <SolidButton
            className="bg-[#5371c0] hover:bg-blue-600"
            textColor="text-white">
            Facebook-kal
          </SolidButton>
        </div>
        <div className="flex justify-center items-start">
          <SolidButton
            className="w-full sm:w-auto bg-[#4184f3] hover:bg-blue-800"
            textColor="text-white">
            Google fiókkal
          </SolidButton>
        </div>
      </div> */}
    </form>
  );
};

export default LoginForm;
